export type HardcodedCatalogCourse = {
  slug: string;
  /**
   * The course this marketing entry describes. Prices are read from its plans rather than
   * written here: this file used to say $25 a level while checkout charged $7.
   */
  course_id: number;
  course_name: string;
  tagline?: string;
  description?: string;
  duration: string;
  mode: string;
  /** Level names shown on the course page, in order. Their prices come from the plans. */
  level_pricing?: Array<{
    level: string;
  }>;
  badge: string;
  curriculum_highlights: string[];
  level_modules?: Array<{
    level: string;
    modules: string[];
  }>;
  requirements?: string[];
  long_description?: string;
  outcomes?: string[];
  schedule?: {
    sessions?: string;
    timing?: string;
    next_cohort_start?: string;
  };
  prerequisites?: string[];
  tools_covered?: string[];
  career_support?: string[];
  target_roles?: string[];
  ideal_for?: string[];
  target_audience?: string;
};

export const HARDCODED_CATALOG_COURSES: HardcodedCatalogCourse[] = [
  {
    slug: "artificial-intelligence-bootcamp",
    course_id: 1,
    course_name: "AI for Engineers",
    tagline: "Go from ML fundamentals to deployment-ready AI systems",
    description:
      "A structured, level-based program (Associate → Intermediate → Advanced) that takes you from core machine learning and deep learning fundamentals to NLP/LLMs, MLOps, deployment, and production-grade AI system design—ending with an end-to-end capstone project.",
    long_description:
      "AI for Engineering is a level-based curriculum designed for engineers who want to build real, production-ready AI systems. You’ll start with machine learning fundamentals and strong data workflows, progress into deep learning for computer vision and NLP/LLMs, and finish with MLOps, deployment strategies, and AI system architecture—plus an end-to-end capstone to pull everything together.",
    requirements: [
      "Comfort with basic programming concepts (variables, functions, loops).",
      "Helpful (not required): basic Python and basic math (algebra, statistics).",
    ],
    outcomes: [
      "Build and evaluate ML models (regression, classification, clustering) with clean workflows.",
      "Prepare datasets with preprocessing, feature engineering, and reproducible experiments.",
      "Implement deep learning fundamentals and apply them to Computer Vision and NLP/LLMs.",
      "Deploy and serve models, understanding practical constraints and trade-offs.",
      "Apply MLOps practices for lifecycle management, pipelines, and monitoring.",
      "Design production-grade AI systems using architecture patterns and real-world considerations.",
      "Deliver an end-to-end capstone AI application.",
    ],
    duration: "3 Months",
    mode: "Online / Live Sessions",
    level_pricing: [
      { level: "Associate" },
      { level: "Intermediate" },
      { level: "Advanced" },
    ],
    badge: "For engineers & technical learners",
    level_modules: [
      {
        level: "Associate",
        modules: [
          "Introduction to AI and Machine Learning",
          "Python and Data Science Tooling",
          "Data Exploration and Preprocessing",
          "Supervised Learning - Regression",
          "Supervised Learning - Classification",
          "Model Evaluation and Improvement",
          "Unsupervised Learning",
          "Neural Networks and Deep Learning Basics",
          "AI Applications and Next Steps",
        ],
      },
      {
        level: "Intermediate",
        modules: [
          "AI Engineering Foundations & Deep Learning Fundamentals",
          "Deep Learning for Computer Vision",
          "Natural Language Processing & Introduction to LLMs",
          "Model Deployment and Serving",
          "MLOps & Model Lifecycle Management",
          "AI System Design & Real-World Considerations",
          "Capstone Project - End-to-End AI Application",
        ],
      },
      {
        level: "Advanced",
        modules: [
          "AI System Architecture and Design Patterns",
          "Data Engineering for AI",
          "Advanced Model Development and Training",
          "Model Optimization and Efficiency",
          "Deployment Strategies and Infrastructure",
          "MLOps and Pipeline Automation",
          "Monitoring and Continuous Improvement",
          "Responsible AI and Security",
          "Case Studies and Capstone Project",
        ],
      },
    ],
    curriculum_highlights: [
      "Build a strong ML foundation (supervised, unsupervised, evaluation)",
      "Master data preprocessing + feature engineering workflows",
      "Understand and implement neural networks + deep learning fundamentals",
      "Apply deep learning to Computer Vision and NLP/LLMs",
      "Deploy and serve models with production constraints in mind",
      "Implement MLOps practices (lifecycle, pipelines, monitoring)",
      "Design AI systems and architecture patterns for real-world use",
      "Ship an end-to-end capstone AI application",
    ],
    career_support: ["Resume/CV Writing", "LinkedIn Optimization", "Freelancing Series", "Interview Preparation"],
  },
  {
    slug: "agentic-ai-bootcamp",
    course_id: 33,
    course_name: "AI for Professionals",
    tagline: "Master AI tools, agents, and workflows for your career",
    description:
      "A level-based program (Beginner → Intermediate → Advanced) for working professionals to confidently use AI tools, write effective prompts, understand how AI systems work, and design reliable agentic workflows—scaling from personal productivity to team-wide adoption and governance.",
    duration: "2 Months",
    mode: "Online",
    level_pricing: [
      { level: "Beginner" },
      { level: "Intermediate" },
      { level: "Advanced" },
    ],
    badge: "For working professionals (Python + APIs)",
    level_modules: [
      {
        level: "Beginner",
        modules: [
          "Introduction to Artificial Intelligence (AI Basics for Everyone)",
          "Using Ready-Made AI Tools (No-Code Introduction)",
          "Prompting Fundamentals",
          "Applied Prompting Skills",
          "Understanding AI Limitations, Errors, and Ethics",
        ],
      },
      {
        level: "Intermediate",
        modules: [
          "How AI Systems Actually Work",
          "How AI Gets Integrated",
          "Agentic AI and Automation",
          "Custom AI Assistants",
          "Designing Reliable AI Workflows",
          "AI Tools for Professional Domains",
        ],
      },
      {
        level: "Advanced",
        modules: [
          "Building Sophisticated Personal AI Systems",
          "Scaling AI Across Teams and Organizations",
          "Scaling AI Across Teams and Organizations (Part 2)",
          "Strategic AI Evaluation and Governance",
          "Leading with AI and Looking Forward",
        ],
      },
    ],
    long_description:
      "AI for Professionals is designed to help you use AI confidently at work—starting with ready-made tools and prompting, then moving into how AI systems and integrations work, and finally into building sophisticated personal AI systems and scaling AI across teams. You’ll learn practical patterns for reliable workflows, agentic automation, evaluation, and governance so your AI adoption stays safe, useful, and measurable.",
    requirements: [
      "No coding required for Beginner level.",
      "For Intermediate/Advanced: comfort with basic tools and workflows (spreadsheets, docs, automations) is helpful.",
    ],
    outcomes: [
      "Use ready-made AI tools effectively and communicate with AI using strong prompting patterns.",
      "Spot common failure modes (hallucinations, bias, privacy risks) and apply safe, ethical practices.",
      "Understand how AI systems work at a high level and how they integrate into real products and workflows.",
      "Design reliable AI workflows and agentic automations tailored to your domain.",
      "Build personal AI systems and scale adoption across teams with evaluation and governance.",
      "Lead AI strategy decisions with clear metrics, guardrails, and a forward-looking roadmap.",
    ],
    schedule: {
      sessions: "Weekly live instructor-led sessions",
      timing: "12:00 PM - 3:00 PM (Saturday & Sunday)",
      next_cohort_start: "9th May",
    },
    prerequisites: [
      "Programming Fundamentals (Python)",
      "Basic understanding of APIs",
      "Eligibility: Currently enrolled or graduated in CS, Engineering, or related fields",
    ],
    tools_covered: [
      "Python",
      "OpenAI API",
      "LangChain",
      "LangGraph",
      "CrewAI",
      "Ollama",
      "vLLM",
      "OpenClaw",
      "n8n",
      "Make.com",
      "FastAPI",
      "Streamlit",
    ],
    curriculum_highlights: [
      "Use ready-made AI tools (no-code) and apply prompting fundamentals",
      "Build applied prompting skills for real workplace tasks",
      "Understand AI limitations, errors, and ethics (privacy, bias, reliability)",
      "Learn how AI systems work and how they get integrated into products",
      "Design reliable agentic workflows and automation patterns",
      "Build personal AI systems and scale AI across teams with governance",
    ],
    career_support: ["AI portfolio", "Freelancing platforms (Upwork, Fiverr)", "Proposal writing for AI projects"],
    ideal_for: ["Aspiring ML Engineers", "Data Scientists", "AI Enthusiasts"],
  },
  {
    slug: "ai-essentials",
    course_id: 35,
    course_name: "AI Essentials",
    tagline: "Build a working understanding of AI concepts and tools",
    description:
      "AI Essentials is a starting point for anyone new to AI and technology. The course introduces the basic concepts of AI, how the technology has evolved, and how it has led to the tools we use today. It is designed to help learners build a basic understanding before moving on to more practical or specialized AI skills.",
    long_description:
      "AI Essentials is a level-based program (Associate → Intermediate → Advanced) for anyone who wants a working understanding of AI, regardless of role or background. You'll start with the basics: what AI actually is, how large language models work, how to prompt them well, and how to check what they give you and use them responsibly. Then you'll apply AI to real work, from research and writing to visuals, data and your career, and finish by building simple workflows, agents and automations of your own.",
    duration: "2 Months",
    mode: "Online",
    level_pricing: [
      { level: "Associate" },
      { level: "Intermediate" },
      { level: "Advanced" },
    ],
    badge: "For beginners — no technical background required",
    level_modules: [
      {
        level: "Associate",
        modules: ["AI Foundations", "Generative AI, Prompting and Trust"],
      },
      {
        level: "Intermediate",
        modules: ["AI for Work and Problem Solving"],
      },
      {
        level: "Advanced",
        modules: ["Building With AI"],
      },
    ],
    requirements: [
      "No prior AI or technical experience needed.",
      "Helpful (not required): basic comfort using a computer and everyday work tools.",
    ],
    outcomes: [
      "Explain what AI is and how it differs from machine learning and data science.",
      "Write clear, effective prompts and avoid common prompting mistakes.",
      "Recognize AI's limitations — hallucinations, bias, and privacy risks — and use it responsibly.",
      "Describe how large language models, embeddings, and retrieval (RAG) actually work.",
      "Design reliable, human-in-the-loop AI workflows for real work tasks.",
      "Evaluate AI opportunities and lead adoption, governance, and strategy across a team or organization.",
    ],
    curriculum_highlights: [
      "Understand what AI is, and how it differs from machine learning and data science",
      "Write clear, simple prompts and avoid common prompting mistakes",
      "Understand AI's limitations, hallucinations, bias, and privacy considerations",
      "Learn how large language models, embeddings, and RAG actually work",
      "Apply role-based, context-aware prompting and choose the right AI tool for the job",
      "Design and evaluate reliable, human-in-the-loop AI workflows",
      "Evaluate AI opportunities, governance, and regulatory considerations",
      "Lead AI adoption and communicate AI strategy to stakeholders",
    ],
  },
  {
    slug: "agentic-ai",
    course_id: 33,
    course_name: "Agentic AI",
    tagline: "Understand how AI agents reason, act, and are kept in check",
    description:
      "Agentic AI covers what separates an agent from a chatbot: the loop of reasoning, acting, and observing, the tools an agent reaches for, and the orchestration that turns one agent into a working system. It ends where production begins — reliability, evaluation, security, and governance.",
    long_description:
      "Agentic AI is a level-based program (Associate → Intermediate → Advanced) for anyone who has used AI tools and now wants to understand the systems being built on top of them. You'll start with what an agent actually is and how one decides what to do next, then build a working agent and orchestrate it into a workflow, and finish with multi-agent systems and the practices that keep them reliable, secure, and accountable.",
    duration: "2 Months",
    mode: "Online",
    level_pricing: [
      { level: "Associate" },
      { level: "Intermediate" },
      { level: "Advanced" },
    ],
    badge: "For anyone building or overseeing AI agents",
    level_modules: [
      {
        level: "Associate",
        modules: ["What Is an Agent?", "How Agents Think and Act"],
      },
      {
        level: "Intermediate",
        modules: ["Building a Working Agent", "Workflows and Orchestration"],
      },
      {
        level: "Advanced",
        modules: ["Multi-Agent Systems", "Reliability, Evaluation, Security, and Governance"],
      },
    ],
    requirements: [
      "Comfort using AI chat tools day to day.",
      "Helpful (not required): having written a prompt you were pleased with, and one that failed.",
    ],
    outcomes: [
      "Explain what makes something an agent rather than a chatbot.",
      "Describe the reason–act–observe loop and where each step goes wrong.",
      "Build a working agent and give it the right tools for a task.",
      "Orchestrate agents into workflows, and know when a workflow is the wrong shape.",
      "Design multi-agent systems and understand how they fail.",
      "Evaluate, secure, and govern agents well enough to put them in front of real work.",
    ],
    curriculum_highlights: [
      "What an agent is, and what it is not",
      "The reason–act–observe loop, and where agents get stuck in it",
      "Tools, memory, and how an agent chooses its next move",
      "Building a working agent end to end",
      "Orchestrating agents into reliable workflows",
      "Multi-agent systems and the coordination problems they create",
      "Evaluating agent behaviour beyond 'it looked right'",
      "Security and governance for agents acting on real systems",
    ],
  },
];

export function getHardcodedCatalogCourses(): HardcodedCatalogCourse[] {
  // Public catalog + cross-links promote the two courses the pricing pages sell;
  // AI for Engineers stays reachable directly but isn't cross-linked yet.
  return HARDCODED_CATALOG_COURSES.filter(
    (c) =>
      c.slug === "ai-essentials" ||
      c.slug === "agentic-ai",
  );
}

export function getHardcodedCatalogCourse(slug: string): HardcodedCatalogCourse | null {
  return HARDCODED_CATALOG_COURSES.find((c) => c.slug === slug) ?? null;
}

type PublicCourseListItem = {
  uuid: string;
  title: string;
  description: string | null;
  cover_image_url: string | null;
  featured_plan: {
    price: string;
    currency: string | null;
    interval: "QUARTER" | "YEAR" | "LIFETIME";
  } | null;
};

type PublicCourseDetail = {
  uuid: string;
  title: string;
  description: string | null;
  cover_image_url: string | null;
  modules: Array<{
    id: number;
    title: string;
    order_index: number;
    levels: { id: number; name: string; level_number: number } | null;
    lessons: Array<{ id: number; uuid: string; title: string; order_index: number }>;
  }>;
};

